import { blockMinutes, MAX_BREAK, sectionBreaks, type Plan, type PlanItem } from '@hoopify/shared';

/**
 * Courtside run state. Times are wall-clock milliseconds, so the countdown is
 * right even if the phone sleeps or the tab is in the background.
 */

export interface RunBlock {
  /** A time slot's id, or `break:<part id>` for a drink break. */
  id: string;
  kind: 'drill' | 'break';
  section: string;
  /** Minutes planned in the session. */
  planned: number;
  /** Minutes after courtside changes. */
  minutes: number;
  skipped: boolean;
  items: PlanItem[];
}

export interface Run {
  sessionId: string;
  version: number;
  blocks: RunBlock[];
  index: number;
  /** When the first drill started. */
  startedAt: number | null;
  blockStartedAt: number | null;
  pausedAt: number | null;
  /** Paused time within the current drill. */
  pausedMs: number;
  finishedAt: number | null;
  /** Actual minutes each drill ran, by block id (for the summary). */
  actual: Record<string, number>;
}

const MIN = 60_000;

export const breakId = (sectionId: string) => `break:${sectionId}`;

/** What to call a step in lists: the drill names, or "Drink break". */
export function stepLabel(b: RunBlock) {
  return b.kind === 'break' ? 'Drink break' : b.items.map((i) => (i.group ? `${i.group}: ${i.title}` : i.title)).join(' · ');
}

export function newRun(sessionId: string, version: number, plan: Plan): Run {
  const breaks = sectionBreaks(plan);
  const blocks: RunBlock[] = plan.sections.flatMap((s, i) => {
    const drills: RunBlock[] = s.blocks.map((b) => ({ id: b.id, kind: 'drill', section: s.title, planned: blockMinutes(b), minutes: blockMinutes(b), skipped: false, items: b.items }));
    return breaks[i] ? [...drills, { id: breakId(s.id), kind: 'break', section: 'Drink break', planned: breaks[i]!, minutes: breaks[i]!, skipped: false, items: [] }] : drills;
  });
  return { sessionId, version, blocks, index: 0, startedAt: null, blockStartedAt: null, pausedAt: null, pausedMs: 0, finishedAt: null, actual: {} };
}

export function start(r: Run, now: number): Run {
  const index = firstActive(r, 0) ?? 0;
  return { ...r, index, startedAt: now, blockStartedAt: now, pausedAt: null, pausedMs: 0 };
}

export function elapsedMs(r: Run, now: number) {
  if (r.blockStartedAt === null) return 0;
  return (r.pausedAt ?? now) - r.blockStartedAt - r.pausedMs;
}

/** Negative once the drill runs over. */
export function remainingMs(r: Run, now: number) {
  const b = r.blocks[r.index];
  return b ? b.minutes * MIN - elapsedMs(r, now) : 0;
}

export function pause(r: Run, now: number): Run {
  return r.pausedAt || r.blockStartedAt === null ? r : { ...r, pausedAt: now };
}

export function resume(r: Run, now: number): Run {
  return r.pausedAt === null ? r : { ...r, pausedMs: r.pausedMs + (now - r.pausedAt), pausedAt: null };
}

function firstActive(r: Run, from: number, dir: 1 | -1 = 1): number | null {
  for (let i = from; i >= 0 && i < r.blocks.length; i += dir) if (!r.blocks[i]!.skipped) return i;
  return null;
}

/** Moves to the next drill that isn't skipped; finishes after the last one. */
export function next(r: Run, now: number): Run {
  const cur = r.blocks[r.index];
  const actual = cur ? { ...r.actual, [cur.id]: Math.round(elapsedMs(r, now) / MIN) } : r.actual;
  const i = firstActive(r, r.index + 1);
  if (i === null) return { ...r, actual, finishedAt: now, pausedAt: null };
  return { ...r, actual, index: i, blockStartedAt: now, pausedAt: null, pausedMs: 0 };
}

export function back(r: Run, now: number): Run {
  const i = firstActive(r, r.index - 1, -1);
  if (i === null) return r;
  return { ...r, index: i, blockStartedAt: now, pausedAt: null, pausedMs: 0, finishedAt: null };
}

/** Drills 1–180 minutes; drink breaks 1–2 (skip a break to drop it). */
export function setMinutes(r: Run, blockId: string, minutes: number): Run {
  return { ...r, blocks: r.blocks.map((b) => (b.id === blockId ? { ...b, minutes: Math.max(1, Math.min(b.kind === 'break' ? MAX_BREAK : 180, Math.round(minutes))) } : b)) };
}

export function toggleSkip(r: Run, blockId: string): Run {
  return { ...r, blocks: r.blocks.map((b) => (b.id === blockId ? { ...b, skipped: !b.skipped } : b)) };
}

/** When the session will end if the rest runs as planned now. */
export function projectedEnd(r: Run, now: number) {
  if (r.finishedAt) return r.finishedAt;
  const rest = r.blocks.slice(r.index + 1).filter((b) => !b.skipped).reduce((n, b) => n + b.minutes, 0);
  return now + Math.max(0, remainingMs(r, now)) + rest * MIN;
}

/**
 * The time the session should finish: the scheduled end if you started roughly
 * on time, otherwise start + length (so a late start isn't counted twice).
 */
export function targetEnd(r: Run, scheduledStart: number, durationMinutes: number) {
  const start = r.startedAt ?? scheduledStart;
  const base = Math.abs(start - scheduledStart) <= 60 * MIN ? scheduledStart : start;
  return base + durationMinutes * MIN;
}

/** Whole minutes behind (positive) or ahead (negative). */
export function minutesBehind(r: Run, now: number, target: number) {
  return Math.round((projectedEnd(r, now) - target) / MIN);
}

/**
 * Shortens the drills still to come, in proportion, so the session ends on
 * time. Never below 1 minute each; the current drill is left alone.
 */
export function fitToTime(r: Run, now: number, target: number): Run {
  const later = r.blocks.map((b, i) => ({ b, i })).filter(({ b, i }) => i > r.index && !b.skipped);
  const laterTotal = later.reduce((n, x) => n + x.b.minutes, 0);
  const available = Math.floor((target - now - Math.max(0, remainingMs(r, now))) / MIN);
  if (!laterTotal || available >= laterTotal) return r;
  const scale = Math.max(0, available) / laterTotal;
  // Round down, then hand the spare minutes to the drills that lost the most to rounding.
  const exact = later.map(({ b }) => ({ id: b.id, raw: b.minutes * scale, mins: Math.max(1, Math.floor(b.minutes * scale)) }));
  let spare = Math.max(0, available) - exact.reduce((n, x) => n + x.mins, 0);
  for (const x of [...exact].sort((a, b) => (b.raw - Math.floor(b.raw)) - (a.raw - Math.floor(a.raw)))) {
    if (spare <= 0) break;
    if (x.raw >= 1) {
      x.mins += 1;
      spare -= 1;
    }
  }
  const sized = new Map(exact.map((x) => [x.id, x.mins]));
  return { ...r, blocks: r.blocks.map((b) => (sized.has(b.id) ? { ...b, minutes: sized.get(b.id)! } : b)) };
}

export function hasChanges(r: Run) {
  return r.blocks.some((b) => b.skipped || b.minutes !== b.planned);
}

/** The plan with courtside timing changes applied: skipped drills removed, minutes scaled per slot. */
export function applyToPlan(plan: Plan, r: Run): Plan {
  const byId = new Map(r.blocks.map((b) => [b.id, b]));
  return {
    sections: plan.sections.map((s) => ({
      ...s,
      breakAfter: (() => {
        const rb = byId.get(breakId(s.id));
        if (!rb) return s.breakAfter;
        return rb.skipped ? 0 : Math.min(MAX_BREAK, rb.minutes);
      })(),
      blocks: s.blocks
        .filter((b) => !byId.get(b.id)?.skipped)
        .map((b) => {
          const rb = byId.get(b.id);
          if (!rb || rb.minutes === rb.planned) return b;
          const scale = rb.minutes / rb.planned;
          return { ...b, items: b.items.map((i) => ({ ...i, minutes: Math.max(1, Math.round(i.minutes * scale)) })) };
        }),
    })),
  };
}

export function clock(ms: number) {
  const neg = ms < 0;
  const s = Math.floor(Math.abs(ms) / 1000);
  return `${neg ? '+' : ''}${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
